<template>
  <!-- Без overflow на контейнере: выноски подсказок выходят за левую границу
       панели, overflow-y: auto их обрезал бы и показал полосу прокрутки. -->
  <!-- Правая панель инструментов редактора: инструменты рисования
       (Линия/Точка/Ластик/Магнит, Отменить/Повторить) и контекстные (выбранный
       отрезок, тип точки, привязка точки). Редкие действия - в меню «Действия»
       нижней панели (RouteEditorActionBar); оттуда же включаются выноски с
       назначением каждой кнопки (useRouteEditorHints). -->
  <div
    class="pointer-events-auto fixed right-1.5 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-1 p-1 rounded-md bg-sheet border border-border shadow-ink"
    role="toolbar"
    aria-orientation="vertical"
    :aria-label="t('map.routeEditor.toolsLabel')"
    data-route-editor-context-panel
  >
    <!-- Слои (подложка и значки) - верхняя кнопка: при построении можно
         показать/спрятать точки-слои. Без relative: поповер позиционируется
         от самой панели, как от .map-controls. -->
    <div class="editor-layers-slot">
      <MapLayersControl :is3D="is3D ?? false" @toggle-3d="$emit('toggle-3d')" />
    </div>
    <div class="h-px bg-[hsl(var(--border)/0.7)] mx-0.5" />
    <template v-for="(item, index) in items" :key="item.key">
      <div v-if="item.separatorBefore && index > 0" class="h-px bg-[hsl(var(--border)/0.7)] mx-0.5" />
      <div class="relative">
        <button
          type="button"
          :data-tool="item.key"
          class="w-8 h-8 rounded-md border flex items-center justify-center transition-colors disabled:opacity-40"
          :class="item.active ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white' : 'border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]'"
          :disabled="item.disabled"
          :title="item.hint"
          :aria-label="item.hint"
          :aria-pressed="item.active === undefined ? undefined : item.active"
          @click="item.onClick"
        >
          <component :is="item.icon" class="w-3.5 h-3.5" :class="item.spin && 'animate-spin'" :stroke-width="1.9" />
        </button>
        <!-- Выноска: справа от кнопки уходит влево, не вылезая за экран -->
        <div
          v-if="hintsOpen"
          class="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-1.5 px-2 py-1 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] text-[11px] font-semibold leading-none whitespace-nowrap shadow-ink"
        >
          {{ item.label }}
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, type Component } from "vue";
import { useI18n } from "vue-i18n";
import {
  Undo2,
  Redo2,
  Slash,
  Magnet,
  Route,
  LocateFixed,
  Eraser,
  Loader2,
} from "lucide-vue-next";
import type { useRouteEditor } from "../composables/useRouteEditor";
import { useRouteEditorHints } from "../composables/useRouteEditorHints";
import LinePlusIcon from "./icons/LinePlusIcon.vue";
import PinPlusIcon from "./icons/PinPlusIcon.vue";
import MapLayersControl from "./MapLayersControl.vue";

const props = defineProps<{
  editor: ReturnType<typeof useRouteEditor>;
  is3D?: boolean;
}>();
defineEmits<{ (e: "toggle-3d"): void }>();

const { t } = useI18n();

interface PanelItem {
  key: string;
  icon: Component;
  // label - короткое название для выноски (в одну строку), hint - полное
  // описание для title/aria-label кнопки.
  label: string;
  hint: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  separatorBefore?: boolean;
  spin?: boolean;
}

const { hintsOpen, closeHints } = useRouteEditorHints();
onBeforeUnmount(closeHints);

const items = computed<PanelItem[]>(() => {
  const e = props.editor;
  const mode = e.mode.value;
  const magnetOn = e.magnetEnabled.value;
  const list: PanelItem[] = [
    { key: "line", icon: LinePlusIcon, label: t("map.routeEditor.toolLine"), hint: t("map.routeEditor.toolLine"), onClick: () => e.toggleMode("line"), active: mode === "line" },
    { key: "waypoint", icon: PinPlusIcon, label: t("map.routeEditor.toolPoint"), hint: t("map.routeEditor.toolPoint"), onClick: () => e.toggleMode("waypoint"), active: mode === "waypoint" },
    { key: "erase", icon: Eraser, label: t("map.routeEditor.toolErase"), hint: t("map.routeEditor.toolErase"), onClick: () => e.toggleMode("erase"), active: mode === "erase" },
    {
      key: "magnet",
      icon: e.routingPending.value ? Loader2 : magnetOn ? Magnet : Slash,
      spin: e.routingPending.value,
      label: t("map.routeEditor.magnetShort"),
      hint: t("map.routeEditor.magnet"),
      onClick: () => e.setMagnet(!magnetOn),
      active: magnetOn,
    },
    { key: "undo", icon: Undo2, label: t("map.routeEditor.labelUndo"), hint: t("map.routeEditor.hintUndo"), onClick: () => e.undo(), disabled: !e.canUndo.value, separatorBefore: true },
    { key: "redo", icon: Redo2, label: t("map.routeEditor.labelRedo"), hint: t("map.routeEditor.hintRedo"), onClick: () => e.redo(), disabled: !e.canRedo.value },
  ];

  // Выбранный тапом отрезок: переключатель "прямая / по тропам" и его длина.
  const leg = e.selectedLegInfo.value;
  if (mode === null && leg) {
    const km = (leg.lengthM / 1000).toFixed(leg.lengthM >= 10000 ? 0 : 1);
    list.push({
      key: "leg",
      icon: leg.routed ? Route : Slash,
      label: leg.routed ? t("map.routeEditor.labelLegRouted") : t("map.routeEditor.labelLegStraight"),
      hint: leg.routed
        ? t("map.routeEditor.hintLegRouted", { km })
        : t("map.routeEditor.hintLegStraight", { km }),
      onClick: () => void e.setLegsRouted(leg.indexes, !leg.routed),
      active: leg.routed,
      separatorBefore: true,
    });
  }

  // Выбранная точка: притянуть к линии/тропе.
  if (mode === null && e.selectedWaypointId.value) {
    list.push({
      key: "snapPoint",
      icon: LocateFixed,
      label: t("map.routeEditor.labelSnapPoint"),
      hint: t("map.routeEditor.hintSnapPoint"),
      onClick: () => e.snapSelectedWaypoint(),
      separatorBefore: true,
    });
  }

  return list;
});
</script>
